/**
 * The reactions this browser has clicked but not yet read back from the server.
 *
 * ## Why not the query cache
 *
 * A pending reaction used to be patched straight into the conversation's message caches.
 * That works for one click at a time and breaks the moment two overlap, in two different
 * ways — and both of them are the same mistake, which is that **the query cache is where
 * the server's answers live, so whatever writes it last wins, regardless of which click
 * is newer**:
 *
 * - Both reaction routes answer with the whole message's reactions AS OF THAT COMMIT. Two
 *   writes in flight means the first response is already out of date when it lands, and
 *   it lands on top of the second click's patch — the pill the viewer just clicked
 *   disappears and comes back a round trip later.
 * - Any thread refetch replaces the cache wholesale, and a refetch carries nothing to
 *   compare against a click. A realtime `message.changed` from someone else, a window
 *   focus, a remount, a mark-read or a send is enough to wipe a pending reaction.
 *
 * Client state is where a not-yet-confirmed reaction belongs. Nothing the server says can
 * erase it, and the pills read `cache + intents` ({@link applyPendingReactions}) so a
 * stale answer brings everyone ELSE's reactions with it while the viewer's own click
 * stays put.
 *
 * ## An intent outlives its own write
 *
 * A write answering only marks the intent `settled`; what ENDS it is
 * {@link reapLandedReactionIntents}, called with the reactions a bubble has rendered.
 * `_libs/pending-reactions` carries the full argument — the short version is that the
 * cache and a component's props disagree for one render, and a click landing in that gap
 * would read the pre-click reactions with no intent left to correct them, and compute the
 * wrong direction. Same contract as {@link ./use-pending-sends}: landing, not "the
 * request answered", is what retires an optimistic row.
 *
 * ## Module scope, not a ref
 *
 * The write loop that consumes these intents outlives the component that started it —
 * `channel-view.tsx` keys the whole view by cid, the office inbox keys it again, and a
 * thread can unmount mid-write — and the same message is rendered by more than one
 * surface (the thread and the pinned strip). A per-component ref would lose its entries
 * on a remount and keep two disagreeing copies; one module-level registry is the single
 * answer for a message.
 *
 * Not persisted: an intent describes a request this page load is making, so it must not
 * survive a reload that has already abandoned that request.
 *
 * The one entry that can outstay its welcome is a settled intent whose bubble unmounted
 * before it could be reaped — a channel switched away from mid-write. It is inert (it
 * says exactly what the server says) and the next visit to that channel reaps it on the
 * first render, so it is a stale object, never a stale pill.
 */

import { create } from "zustand";

import type { ChannelMessageReaction } from "../_api/channel-endpoints";
import {
  NO_REACTION_INTENTS,
  type ReactionIntents,
  reapSettledIntents,
} from "../_libs/pending-reactions";

/** One message's intents, across every surface that draws it. */
function messageKey(cid: string, messageId: string): string {
  return `${cid}:${messageId}`;
}

type PendingReactionsStore = {
  byMessage: Record<string, ReactionIntents | undefined>;
  intend: (key: string, type: string, reacted: boolean) => number;
  settleIfSeq: (key: string, type: string, seq: number) => void;
  reapLanded: (
    key: string,
    reactions: ChannelMessageReaction[],
    viewerId: string,
  ) => void;
  clearAll: (key: string) => void;
};

/**
 * Private — the module-level writers below are the whole surface, so nothing can reach
 * past them into `setState`.
 *
 * Every action spreads `byMessage` but replaces only its own message's record, so a click
 * on one bubble leaves every other bubble's record identity untouched and those clusters
 * never re-render for it.
 */
const usePendingReactionsStore = create<PendingReactionsStore>()(
  (set, get) => ({
    byMessage: {},

    intend: (key, type, reacted) => {
      const current = get().byMessage[key];
      // Per key, not a module counter: the seq only has to be unique within one
      // (message, emoji) for `settleIfSeq` to tell its own intent from a newer one, and a
      // module-level counter would be hidden mutable state. Safe to restart at 1 after the
      // record is reaped, because only the one running write loop ever settles — and it
      // settles synchronously after its own await, so no stale settle outlives the reap.
      const seq = (current?.[type]?.seq ?? 0) + 1;

      set((state) => ({
        byMessage: {
          ...state.byMessage,
          [key]: { ...current, [type]: { reacted, seq, settled: false } },
        },
      }));

      return seq;
    },

    settleIfSeq: (key, type, seq) => {
      const current = get().byMessage[key];
      const intent = current?.[type];
      // A newer click replaced this intent while the write was in flight. It owns the pill
      // now, and the loop that is about to re-read the record will write it next.
      if (!intent || intent.seq !== seq || intent.settled) return;

      set((state) => ({
        byMessage: {
          ...state.byMessage,
          [key]: { ...current, [type]: { ...intent, settled: true } },
        },
      }));
    },

    reapLanded: (key, reactions, viewerId) => {
      const current = get().byMessage[key];
      if (!current) return;

      const remaining = reapSettledIntents(current, reactions, viewerId);
      // Same record back means nothing landed. Skipping the `set` is what lets a bubble
      // run this on every render without notifying every other bubble.
      if (remaining === current) return;

      set((state) => {
        const byMessage = { ...state.byMessage };
        // Dropped, not left empty: otherwise a long session accumulates one dead record
        // per message anyone ever reacted to.
        if (Object.keys(remaining).length > 0) byMessage[key] = remaining;
        else delete byMessage[key];
        return { byMessage };
      });
    },

    clearAll: (key) => {
      if (!get().byMessage[key]) return;

      set((state) => {
        const byMessage = { ...state.byMessage };
        delete byMessage[key];
        return { byMessage };
      });
    },
  }),
);

/** Intend `reacted` for this emoji; returns the seq that claims the intent. */
export function setReactionIntent(
  cid: string,
  messageId: string,
  type: string,
  reacted: boolean,
): number {
  return usePendingReactionsStore
    .getState()
    .intend(messageKey(cid, messageId), type, reacted);
}

/**
 * Record that the server agreed to this emoji's intent, unless a newer click has already
 * replaced it.
 *
 * Settling is NOT forgetting — the intent keeps painting the pill until
 * {@link reapLandedReactionIntents} sees the rendered reactions carry it. See the module
 * doc for the one-render gap that closes.
 */
export function settleReactionIntentIfSeq(
  cid: string,
  messageId: string,
  type: string,
  seq: number,
): void {
  usePendingReactionsStore
    .getState()
    .settleIfSeq(messageKey(cid, messageId), type, seq);
}

/**
 * Forget the settled intents these rendered reactions have caught up with.
 *
 * Returns without notifying when nothing landed, so a bubble may call it on every render.
 * Only the server's own answer makes an intent eligible, so a stale cache that happens to
 * agree never retires a click that is still owed a write.
 */
export function reapLandedReactionIntents(
  cid: string,
  messageId: string,
  reactions: ChannelMessageReaction[],
  viewerId: string,
): void {
  usePendingReactionsStore
    .getState()
    .reapLanded(messageKey(cid, messageId), reactions, viewerId);
}

/**
 * Forget every intent on this message — the write failed, and the whole message snaps
 * back to what the server last said.
 *
 * The whole message rather than the one emoji that failed: a burst against a frozen
 * channel or an offline browser fails identically for all of them, and one snap-back with
 * one toast is the honest report.
 */
export function clearReactionIntents(cid: string, messageId: string): void {
  usePendingReactionsStore.getState().clearAll(messageKey(cid, messageId));
}

/**
 * This message's intents, for a caller that is not rendering.
 *
 * A click handler and the write loop both read through this rather than through a render
 * snapshot — two clicks in one frame share one `message` prop, and the loop runs after
 * its component may already be gone.
 */
export function readReactionIntents(
  cid: string,
  messageId: string,
): ReactionIntents {
  return (
    usePendingReactionsStore.getState().byMessage[messageKey(cid, messageId)] ??
    NO_REACTION_INTENTS
  );
}

/** This message's intents, subscribed. */
export function usePendingReactions(
  cid: string,
  messageId: string,
): ReactionIntents {
  // One key, never the whole record — see the store's note on identity.
  return usePendingReactionsStore(
    (state) =>
      state.byMessage[messageKey(cid, messageId)] ?? NO_REACTION_INTENTS,
  );
}
